import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';
import {
  cartRequestWatcher,
  dispatchCartError,
  dispatchCartUpdate,
  fetchCart,
  postCart,
  userErrorMessage,
} from '@theme/qallo-cart';
import {
  BUNDLE_PROPERTY,
  BUNDLE_IMAGE_PROPERTY,
  BUNDLE_SOURCE_PROPERTY,
  BUNDLE_TYPE_PROPERTY,
  createBoxId,
  discountedPrice,
  pctForCount,
} from '@theme/qallo-mix-match';

/*
 * Qallo: Bundle & Save op de productpagina van tubs (blocks/qallo-bundle-save.liquid; zakjes krijgen geen blok).
 * - Liquid rendert alle kaarten, rijen, keuzelijsten en prijzen; deze module werkt de keuze, de prijzen en het
 *   winkelmandje bij. Een bundel is altijd het product van de pagina (zelfde smaak); smaken mixen gebeurt in de builder.
 *   Per tub een keuze 30 of 60 porties.
 * - 1 gekozen: niets anders dan normaal, het productformulier van het theme voegt toe.
 * - 2 of meer: de submit van het productformulier van deze sectie wordt in de capture-fase op window onderschept
 *   (vóór de gedelegeerde on:submit van @theme/component op document), zodat de knop, het label en de sticky knop
 *   van het theme blijven werken. De stuks gaan zoals in de builder (assets/qallo-mix-match.js) naar /cart/add.js met
 *   _qallo_bundle (nieuwe box-ID), _qallo_bundle_type en _qallo_bundle_source 'pdp' (geen "Box bewerken" in het
 *   winkelmandje); daarna één CartLinesUpdateEvent met section-HTML, zodat de
 *   drawer opent. Snelle checkout en aantal zijn verborgen via data-qallo-bundle op het productformulier.
 */

/**
 * @typedef {object} BundleVariant
 * @property {number} id
 * @property {string} label
 * @property {number} price - in centen
 * @property {boolean} available
 * @property {string} bundleImage - Absolute https-URL (600px) voor _qallo_bundle_image, leeg als er geen foto is
 * @property {string} thumb - Miniatuur in de rij (120px), leeg als er geen foto is
 * @property {string} thumbSrcset - srcset van de miniatuur (40, 80 en 120px als 1x, 2x en 3x)
 */

/**
 * @typedef {object} BundleConfig
 * @property {'tubs'} type
 * @property {{ min: number, pct: number }[]} tiers
 * @property {string} moneyFormat
 * @property {string} currency
 * @property {number} productId
 * @property {number} bundleVariantId
 * @property {number} pageVariantPrice
 * @property {BundleVariant[]} variants - Varianten van dit product in de bundel
 * @property {Record<string, string>} strings
 */

/**
 * @typedef {object} Refs
 * @property {HTMLScriptElement} config
 * @property {HTMLElement[]} cards
 * @property {HTMLElement} [error]
 * @property {HTMLElement} [status]
 */

/**
 * @extends {Component<Refs>}
 */
class QalloBundleSave extends Component {
  requiredRefs = ['config', 'cards'];

  /** @type {BundleConfig | null} */
  #config = null;

  #boxId = '';

  #count = 1;

  #busy = false;

  /** Variant van het product van de pagina in de bundel (tubs: de gekozen variant). */
  #pageVariantId = '';

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    try {
      this.#config = JSON.parse(this.refs.config.textContent || '{}');
    } catch (error) {
      console.warn('[qallo-bundle-save] Invalid configuration', error);
      this.#config = null;
      return;
    }
    const config = this.#config;
    if (!config?.type) return;
    // Quick Add-modal: daar is het blok verborgen (CSS), geen onderschepping
    if (this.closest('.quick-add-modal')) return;

    if (!this.#boxId) this.#boxId = createBoxId(config.type);
    this.#pageVariantId = String(config.bundleVariantId ?? '');

    // Capture op window loopt vóór de capture-listener van @theme/component op document
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    this.#section()?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Keuze herstellen na terug-navigatie (bfcache of autocomplete van de browser). Chrome zet het formulier pas na
    // connectedCallback terug, zonder change-event: daarom ook na load en pageshow opnieuw lezen (en bij toevoegen).
    this.#syncFromForm();
    window.addEventListener('pageshow', () => this.#syncFromForm(), { signal });
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.#syncFromForm(), { once: true, signal });
    }
  }

  /** Aantal en rijen gelijkzetten met wat het formulier toont (gekozen kaart, portiekeuze per rij). */
  #syncFromForm() {
    const checked = this.querySelector('.qbs-card__radio:checked');
    this.#count = checked instanceof HTMLInputElement ? Number(checked.value) || 1 : 1;
    for (const card of this.refs.cards ?? []) {
      for (const row of this.#rowsOf(card)) this.#setThumb(row, this.#variantOfRow(row));
    }
    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#setBundleMode(false);
    this.#renderStickyPrice(null);
  }

  /* ---------- Handlers (on:change in de Liquid) ---------- */

  /** @param {Event} event */
  select(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;
    const next = Number(input.value) || 1;
    const previous = this.#count;
    if (next === previous) return;

    // Portiekeuzes van de vorige kaart overnemen voor de rijen die beide kaarten hebben (tubs)
    const from = this.#cardFor(previous);
    const to = this.#cardFor(next);
    if (from && to && previous > 1 && next > 1) {
      const fromRows = this.#rowsOf(from);
      const toRows = this.#rowsOf(to);
      for (let index = 0; index < Math.min(fromRows.length, toRows.length); index++) {
        const variantId = this.#variantOfRow(fromRows[index]);
        if (variantId) this.#setRow(toRows[index], variantId);
      }
    }

    this.#count = next;
    this.#hideError();
    this.#render();
  }

  /** @param {Event} event */
  changePortions(event) {
    const row = event.target instanceof Element ? event.target.closest('[data-item]') : null;
    if (row instanceof HTMLElement) this.#setThumb(row, this.#variantOfRow(row));
    this.#hideError();
    this.#render();
  }

  /* ---------- Winkelmandje ---------- */

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = this.#productForm()?.querySelector('form');
    if (!form || event.target !== form) return;
    // Toevoegen volgt altijd de kaart die de klant ziet (ook na een teruggezet formulier)
    this.#syncFromForm();
    if (this.#count < 2) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addBundle();
  };

  async #addBundle() {
    const config = this.#config;
    const card = this.#cardFor(this.#count);
    if (!config || !card || this.#busy) return;

    // Eén lijn per variant met het aantal, zoals de builder; alle lijnen met dezelfde box-ID
    /** @type {Map<string, number>} */
    const quantities = new Map();
    for (const variantId of this.#variantsOf(card)) {
      if (!variantId) continue;
      quantities.set(variantId, (quantities.get(variantId) ?? 0) + 1);
    }
    // _qallo_bundle_image: foto van de gekozen variant, de Cart Transform gebruikt die voor de samengevoegde lijn
    const items = [...quantities].map(([variantId, quantity]) => ({
      id: Number(variantId),
      quantity,
      properties: {
        [BUNDLE_PROPERTY]: this.#boxId,
        [BUNDLE_TYPE_PROPERTY]: config.type,
        [BUNDLE_SOURCE_PROPERTY]: 'pdp',
        [BUNDLE_IMAGE_PROPERTY]: this.#variantFor(variantId)?.bundleImage ?? '',
      },
    }));
    if (items.length === 0) return;

    const strings = config.strings;
    this.#setBusy(true);
    /** @type {Record<string, string> | undefined} */
    let sections;
    try {
      const response = await postCart(Theme.routes.cart_add_url, { items });
      sections = response?.sections;
    } catch (error) {
      console.warn('[qallo-bundle-save] Cart add failed', error);
      dispatchCartError(this, error, strings.error ?? '');
      this.#showError(userErrorMessage(error, strings.error ?? ''));
      this.#setBusy(false);
      return;
    }

    const change = {
      action: /** @type {const} */ ('add'),
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
    };
    try {
      const cart = await fetchCart();
      dispatchCartUpdate(this, change, cart, sections, 'qallo-bundle-save');
    } catch (error) {
      // Enkel /cart.js bleef falen (bv. 429): drawer en teller later verversen
      console.warn('[qallo-bundle-save] Cart refresh failed', error);
      cartRequestWatcher.refreshLater(true);
    }

    // Volgende toevoeging is een nieuwe box
    this.#boxId = createBoxId(config.type);
    this.#announce(strings.added ?? '');
    this.#setBusy(false);
  }

  /**
   * Variantwissel op de pagina (bv. 30 naar 60 porties): prijs van de eerste kaart en, voor tubs, de rijen die nog op
   * de vorige variant van dit product stonden.
   * @param {Event} event
   */
  #onProductSelect = async (event) => {
    const config = this.#config;
    if (!config) return;

    // Meteen (niet pas na het ophalen van de section): de rijen naar de nieuwe variant, anders voegt een snelle klik
    // op toevoegen nog de vorige portiegrootte toe
    const detail = /** @type {any} */ (event).detail;
    const syncId = String(detail?.detail?.variantId ?? detail?.variantId ?? '');
    if (syncId && config.variants.some((variant) => String(variant.id) === syncId)) {
      this.#moveRows(syncId);
      this.#render();
    }

    const promise = /** @type {any} */ (event).promise;
    if (!promise) return;

    let resource;
    try {
      ({ detail: { resource } = {} } = (await promise) ?? {});
    } catch {
      return;
    }
    if (!resource?.id || String(resource.product_id ?? config.productId) !== String(config.productId)) return;

    if (Number.isFinite(Number(resource.price))) config.pageVariantPrice = Number(resource.price);

    const newId = String(resource.id);
    if (config.variants.some((variant) => String(variant.id) === newId)) this.#moveRows(newId);
    this.#render();
    // Na Horizon (die de sticky prijs in zijn eigen then vervangt): opnieuw
    setTimeout(() => this.#render(), 0);
  };

  /**
   * Rijen die nog op de vorige variant van de pagina stonden naar de nieuwe variant.
   * @param {string} newId
   */
  #moveRows(newId) {
    const previous = this.#pageVariantId;
    if (previous === newId) return;
    for (const card of this.refs.cards) {
      for (const row of this.#rowsOf(card)) {
        if (this.#variantOfRow(row) === previous) this.#setRow(row, newId);
      }
    }
    this.#pageVariantId = newId;
  }

  /* ---------- Rijen ---------- */

  /** @param {number} count */
  #cardFor(count) {
    return this.refs.cards.find((card) => Number(card.dataset.count) === count) ?? null;
  }

  /** @param {HTMLElement} card */
  #rowsOf(card) {
    return /** @type {HTMLElement[]} */ ([...card.querySelectorAll('[data-item]')]);
  }

  /**
   * Varianten van de tubs in een kaart: de portiekeuze per rij.
   * @param {HTMLElement} card
   * @returns {string[]}
   */
  #variantsOf(card) {
    return this.#rowsOf(card).map((row) => this.#variantOfRow(row));
  }

  /** @param {HTMLElement} row */
  #variantOfRow(row) {
    const portions = row.querySelector('[data-portions]');
    return portions instanceof HTMLSelectElement ? portions.value : '';
  }

  /** @param {string} variantId */
  #variantFor(variantId) {
    return (this.#config?.variants ?? []).find((variant) => String(variant.id) === variantId) ?? null;
  }

  /**
   * @param {HTMLElement} row
   * @param {string} variantId
   */
  #setRow(row, variantId) {
    const portions = row.querySelector('[data-portions]');
    if (portions instanceof HTMLSelectElement) portions.value = variantId;
    this.#setThumb(row, variantId);
  }

  /**
   * Miniatuur van de gekozen variant: src en srcset uit de configuratie (geen fetch). Zonder foto blijft het lege vak.
   * @param {HTMLElement} row
   * @param {string} variantId
   */
  #setThumb(row, variantId) {
    const image = row.querySelector('.qbs-item__image');
    if (!(image instanceof HTMLImageElement)) return;
    const variant = this.#variantFor(variantId);
    if (!variant?.thumb) {
      image.hidden = true;
      return;
    }
    if (image.getAttribute('src') !== variant.thumb) {
      image.srcset = variant.thumbSrcset ?? '';
      image.src = variant.thumb;
    }
    image.hidden = false;
  }

  /* ---------- Weergave ---------- */

  #render() {
    const config = this.#config;
    if (!config) return;

    for (const card of this.refs.cards) {
      const count = Number(card.dataset.count) || 1;
      const selected = count === this.#count;
      card.toggleAttribute('data-selected', selected);
      const items = card.querySelector('[data-items]');
      if (items instanceof HTMLElement) items.hidden = !selected;

      let subtotal = config.pageVariantPrice;
      let pct = 0;
      if (count > 1) {
        subtotal = 0;
        for (const variantId of this.#variantsOf(card)) {
          subtotal += this.#variantFor(variantId)?.price ?? 0;
        }
        pct = pctForCount(config.tiers, count);
      }
      const current = card.querySelector('[data-price-current]');
      if (current) current.textContent = this.#money(discountedPrice(subtotal, pct));
      const compare = card.querySelector('[data-price-compare]');
      if (compare) compare.textContent = this.#money(subtotal);
      if (selected) this.#renderStickyPrice(count > 1 ? { now: discountedPrice(subtotal, pct), was: subtotal } : null);
    }

    this.#setBundleMode(this.#count > 1);
  }

  /**
   * Sticky toevoegbalk onderaan (sections/product-information.liquid): in bundelmodus de bundelprijs i.p.v. de prijs
   * van één tub, en geen aantal op de knop. De originele prijs blijft in de DOM (Horizon werkt ze bij), verborgen.
   * @param {{ now: number, was: number } | null} price
   */
  #renderStickyPrice(price) {
    const section = this.#section();
    const sticky = section?.querySelector('sticky-add-to-cart');
    sticky?.toggleAttribute('data-qallo-bundle', Boolean(price));
    const container = section?.querySelector('.sticky-add-to-cart__price');
    if (!(container instanceof HTMLElement)) return;
    container.querySelector('.qbs-sticky-price')?.remove();
    container.classList.toggle('qbs-sticky-priced', Boolean(price));
    if (!price) return;

    const wrapper = document.createElement('span');
    wrapper.className = 'qbs-sticky-price';
    const now = document.createElement('span');
    now.className = 'price';
    now.textContent = this.#money(price.now);
    wrapper.append(now);
    if (price.was > price.now) {
      const was = document.createElement('s');
      was.className = 'qbs-sticky-price__was';
      was.textContent = this.#money(price.was);
      wrapper.append(was);
    }
    container.prepend(wrapper);
  }

  /** @param {boolean} active */
  #setBundleMode(active) {
    this.#productForm()?.toggleAttribute('data-qallo-bundle', active);
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('aria-busy', busy);
    const button = this.#productForm()?.querySelector('button[name="add"]');
    if (button) button.setAttribute('aria-busy', String(busy));
  }

  /** @param {number} cents */
  #money(cents) {
    const config = this.#config;
    if (!config) return String(cents);
    return formatMoney(cents, config.moneyFormat, config.currency);
  }

  #section() {
    return this.closest('.shopify-section');
  }

  /** Het productformulier van deze sectie (blocks/buy-buttons.liquid). */
  #productForm() {
    const form = this.#section()?.querySelector('product-form-component');
    return form instanceof HTMLElement ? form : null;
  }

  /** @param {string} text */
  #announce(text) {
    const status = this.refs.status;
    if (!status || !text) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = text;
    });
  }

  /** @param {string} text */
  #showError(text) {
    const error = this.refs.error;
    if (!error || !text) return;
    error.textContent = text;
    error.hidden = false;
  }

  #hideError() {
    const error = this.refs.error;
    if (!error || error.hidden) return;
    error.hidden = true;
    error.textContent = '';
  }
}

if (!customElements.get('qallo-bundle-save')) {
  customElements.define('qallo-bundle-save', QalloBundleSave);
}
